import { createFileRoute } from "@tanstack/react-router";
import { pageHead, localizedUrl } from "@/lib/seo";

const TITLE = "Wat kost een website laten maken in 2026? Vaste prijzen & ranges | IT Solutions";
const DESC =
  "Wat kost een website laten maken? Realistische prijsranges per type site (van €1.500 tot €50.000+), wat de prijs bepaalt en onze jaarpakketten vanaf €990 en projectpakketten vanaf €9.950.";
const PATH = "/blog/wat-kost-een-website-laten-maken";
const PUBLISHED = "2026-07-11";
const UPDATED = "2026-07-11";

type PriceRow = {
  type: string;
  range: string;
  min: number;
  max: number;
  who: string;
};

const PRICE_ROWS: PriceRow[] = [
  {
    type: "One-pager / landingspagina",
    range: "€1.500 – €3.500",
    min: 1500,
    max: 3500,
    who: "Zelfstandigen, event- of campagnepagina, MVP-launch.",
  },
  {
    type: "Brochure-website (5–10 pagina's)",
    range: "€3.500 – €7.500",
    min: 3500,
    max: 7500,
    who: "Kleine bedrijven die professioneel online willen met basis SEO en CMS.",
  },
  {
    type: "Zakelijke website met CMS & SEO",
    range: "€7.500 – €15.000",
    min: 7500,
    max: 15000,
    who: "KMO's die inhoud zelf willen beheren en zichtbaar willen zijn in Google.",
  },
  {
    type: "Meertalige site of complexe integraties",
    range: "€12.000 – €25.000",
    min: 12000,
    max: 25000,
    who: "Bedrijven met meerdere talen, CRM/ERP-koppelingen of klantportaal.",
  },
  {
    type: "Webshop (Shopify / custom)",
    range: "€6.000 – €30.000",
    min: 6000,
    max: 30000,
    who: "E-commerce vanaf enkele producten tot volledige multi-store setup.",
  },
  {
    type: "Enterprise / maatwerk platform",
    range: "€25.000 – €80.000+",
    min: 25000,
    max: 80000,
    who: "Custom applicaties, hoge performance-eisen, complexe workflows.",
  },
];

const FAQ: { q: string; a: string }[] = [
  {
    q: "Wat kost een eenvoudige website gemiddeld?",
    a: "Een professionele one-pager kost gemiddeld €1.500 tot €3.500. Een klassieke bedrijfssite met 5 tot 10 pagina's, CMS en basis SEO zit tussen €3.500 en €7.500.",
  },
  {
    q: "Waarom lopen prijzen zo uiteen?",
    a: "De prijs wordt bepaald door aantal pagina's, maatwerkdesign, CMS-keuze, meertaligheid, koppelingen (CRM/ERP/betalingen), SEO-diepgang en of je kiest voor eigendom of een jaarabonnement.",
  },
  {
    q: "Wat is het verschil tussen eenmalig kopen en een abonnement?",
    a: "Eenmalig betalen betekent dat de site je eigendom is. Een abonnement (vanaf €749/jaar bij IT Solutions) spreidt de kost en omvat hosting, SSL, updates, back-ups en doorlopende SEO.",
  },
  {
    q: "Zijn hosting, domein en SSL inbegrepen?",
    a: "Bij onze pakketten zijn EU-hosting, SSL, back-ups en SEO-basis altijd inbegrepen. Bij veel goedkopere aanbieders komt dit als extra kost bovenop de bouwprijs.",
  },
  {
    q: "Hoe lang duurt de bouw van een website?",
    a: "Een Local Hero-site is doorgaans klaar in 2 tot 3 weken. Business Architectuur duurt 4 tot 6 weken en enterprise-projecten variëren van 8 tot 16 weken afhankelijk van de scope.",
  },
  {
    q: "Krijg ik een vaste prijs of een uurtarief?",
    a: "Wij werken uitsluitend met vaste pakketprijzen (jaarpakketten vanaf €990/jaar, projectpakketten vanaf €9.950). Zo weet je op voorhand exact wat je betaalt — nul verrassingen.",
  },
];

import { BlogWatKostPage } from "@/pages/blog-wat-kost-een-website-laten-maken";

export const Route = createFileRoute("/blog/wat-kost-een-website-laten-maken")({
  head: () => {
    const head = pageHead({
      lang: "nl",
      pathWithoutLang: PATH,
      title: TITLE,
      description: DESC,
      ogType: "article",
    });
    const url = localizedUrl(PATH, "nl");
    const articleLd = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "Wat kost een website laten maken in 2026?",
      description: DESC,
      image: ["https://www.itsolutions.be/og/blog-wat-kost.jpg"],
      inLanguage: "nl-BE",
      datePublished: PUBLISHED,
      dateModified: UPDATED,
      mainEntityOfPage: { "@type": "WebPage", "@id": url },
      author: { "@id": "https://www.itsolutions.be/#organization" },
      publisher: { "@id": "https://www.itsolutions.be/#organization" },
    };
    const faqLd = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: FAQ.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    };
    return {
      ...head,
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(articleLd) },
        { type: "application/ld+json", children: JSON.stringify(faqLd) },
      ],
    };
  },
  component: BlogWatKostPage,
});
